import { goClient } from "../config/apiClient";
import { ProcessResult } from "../../types/output_vu";
import { ProcessResultVh } from "types/output_vh";

import { useAuthStore } from "store/authStore";

const BACKEND_URL = import.meta.env.VITE_GO_BACKEND_URL;

interface ProgressState {
  step: "idle" | "upload" | "download" | "complete" | "error";
  percent: number;
  message?: string; // 텍스트 메시지 표시용
}

interface HandleSubmitParams {
  file?: File;
  youtubeUrl?: string;
  setProgress: (
    progress: ProgressState | ((prev: ProgressState) => ProgressState)
  ) => void;
  projectId: string;
  instrumentsString?: string;
  num_speakers?: number;
  signal: AbortSignal;
}

// handleSubmit의 반환 타입을 명확히 하기 위한 공용 타입
type AnyProcessResult = ProcessResult | ProcessResultVh;

// SSE 응답 타입 정의
interface SSERunningData {
  completedFlows: number;
  currentStep: string;
  flowName: string;
  message: string;
  percentage: string;
  progress: number;
  totalFlows: number;
}

interface SSECompletedData {
  documentId: string;
  filePath: string;
  flowingTime: number;
  output: AnyProcessResult;
  resultPath: string;
  thumbnailPath: string;
  uploadFilename: string;
  youtubeInfo: any;
}

interface SSEFailedData {
  error: string;
  [key: string]: any;
}

interface SSEResponse {
  status: "running" | "completed" | "failed" | "ping";
  data?: SSERunningData | SSECompletedData | SSEFailedData;
}

// 초기 작업 시작 응답 타입
interface FlowStartResponse {
  status: string;
  message: string;
  content: {
    documentId: string;
    status: string;
    message: string;
  };
}

/*
    Flow 실행 API 호출 (SSE 방식)

    params:
    - file: 업로드할 파일 (선택 사항)
    - youtubeUrl: YouTube URL (선택 사항)
    - setProgress: 단계별 진행 상태 변경 함수
    - projectId: 프로젝트 ID
    - instrumentsString: 악기 정보 (선택 사항)
    - num_speakers: 화자 수 (선택 사항)
    - signal: AbortSignal

    logic:
    1. 파일과 URL이 모두 없으면 경고 메시지 출력 후 종료
    2. FormData 객체 생성
    3. 첫 번째 POST 요청으로 작업 시작 및 documentId 받기
    4. 두 번째 SSE 연결로 실시간 진행 상황 스트리밍
    5. SSE 데이터 파싱 및 진행률 업데이트
    6. 완료 시 결과 반환, 실패 시 에러 처리
*/

// SSE 스트림에서 데이터를 파싱하는 헬퍼 함수
const parseSSEData = (buffer: string): { events: SSEResponse[], remainingBuffer: string } => {
  const events: SSEResponse[] = [];
  const eventBlocks = buffer.split('\n\n'); // SSE 이벤트는 \n\n으로 구분됨
  
  // 마지막 블록은 불완전할 수 있으므로 별도로 처리
  const remainingBuffer = eventBlocks.pop() || '';
  
  for (const block of eventBlocks) {
    const lines = block.split('\n');
    
    for (const line of lines) {
      const trimmedLine = line.trim();
      if (trimmedLine.startsWith('data: ')) {
        try {
          const data = trimmedLine.substring(6); // 'data: ' 제거
          if (data) {
            const parsed = JSON.parse(data) as SSEResponse;
            events.push(parsed);
          }
        } catch (e) {
          console.warn('Failed to parse SSE data:', trimmedLine);
        }
      }
    }
  }
  
  return { events, remainingBuffer };
};

// SSE 방식 Flow 실행
export const handleSubmit = async ({
  file,
  youtubeUrl,
  setProgress,
  projectId,
  instrumentsString,
  num_speakers,
  signal,
}: HandleSubmitParams): Promise<AnyProcessResult | null> => {
  if (!file && !youtubeUrl) {
    alert("Please select a file or enter a YouTube URL.");
    return null;
  }

  // 1단계: 작업 시작 요청
  const formData = new FormData();
  // URL은 더 이상 필요 없음 (ApiClient의 baseURL 사용)

  if (file) {
    formData.append("file", file);
    formData.append("inputType", "file");
  } else if (youtubeUrl) {
    formData.append("input", youtubeUrl);
    formData.append("inputType", "url");
  }

  if (instrumentsString) {
    formData.append("instruments", instrumentsString);
  }

  if (num_speakers) {
    formData.append("num_speakers", num_speakers.toString());
  }

  setProgress({ step: "upload", percent: 0, message: "서버로 파일 업로드 중..." });

  try {
    // 첫 번째 요청: 작업 시작 및 documentId 받기 (Go 백엔드)
    const startResponse = await goClient.post<FlowStartResponse>(
      `/flow/${projectId}`,
      formData,
      {
        signal,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    const documentId = startResponse.data.content.documentId;
    console.log("Flow started with documentId:", documentId);

    // POST 요청 완료 - SSE 연결 준비
    setProgress({ step: "upload", percent: 0, message: "처리 준비 중..." });

    // 2단계: SSE 스트림 연결 (fetch 사용, 토큰은 ApiClient에서 가져옴)
    const streamUrl = `${BACKEND_URL}/flow/${documentId}/stream`;
    const token = useAuthStore.getState().accessToken;
    
    const response = await fetch(streamUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache',
      },
      signal,
    });

    if (!response.ok) {
      throw new Error(`SSE connection failed: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    // 스트림 리더 생성
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result: AnyProcessResult | null = null;

    // SSE 스트림 연결 완료 - 퍼센트 표시 시작
    setProgress({ step: "download", percent: 0, message: undefined });

    try {
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          break;
        }

        // 청크를 디코딩하고 버퍼에 추가
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        // 완전한 이벤트들 파싱
        const { events, remainingBuffer } = parseSSEData(buffer);
        buffer = remainingBuffer; // 처리되지 않은 부분만 버퍼에 남김
        
        for (const event of events) {
          if (event.status === 'ping') {
            // 하트비트 무시
            continue;
          }

          if (event.status === 'running' && event.data) {
            // 타입 가드를 통해 running 데이터인지 확인
            const runningData = event.data as SSERunningData;
            if (typeof runningData.progress === 'number') {
              // SSE에서 받은 progress를 그대로 사용 (0~100%)
              const progress = Math.min(Math.max(runningData.progress, 0), 100);
              console.log(`Progress: ${runningData.currentStep} - ${Math.round(progress)}%`);
              setProgress({ step: "download", percent: Math.round(progress), message: undefined });
            }
          }

          if (event.status === 'completed') {
            // 작업 완료
            console.log("Flow completed");
            console.log("Event data:", event.data);
            setProgress({ step: "complete", percent: 100, message: undefined });
            
            // 결과 데이터 처리
            if (event.data) {
              const completedData = event.data as SSECompletedData;
              console.log("Completed data:", completedData);
              
              // 기본 결과 객체 생성
              result = {
                documentId: completedData.documentId,
                filePath: completedData.filePath,
                uploadFilename: completedData.uploadFilename,
                resultPath: completedData.resultPath,
                thumbnailPath: completedData.thumbnailPath,
                youtubeInfo: completedData.youtubeInfo,
                output: completedData.output,
              } as any; // 임시로 any 사용하되 구조적으로 안전함
              
              console.log("Created result:", result);
              
              // speakerSegments를 segments로 변환 (기존 로직 유지)
              if (result && result.output) {
                const outputData = result.output as any;
                if (outputData.speakerSegments) {
                  outputData.segments = outputData.speakerSegments;
                  delete outputData.speakerSegments;
                }
                // utterances가 있다면 그것도 segments로 처리
                if (outputData.utterances) {
                  outputData.segments = outputData.utterances;
                }
              }
            } else {
              console.warn("No event.data found in completed event");
            }
            
            console.log("Returning result:", result);
            return result;
          }

          if (event.status === 'failed') {
            // 작업 실패
            const failedData = event.data as SSEFailedData;
            const errorMessage = failedData?.error || 'Flow execution failed';
            console.error("Flow failed:", errorMessage);
            setProgress({ step: "error", percent: 0, message: errorMessage });
            throw new Error(errorMessage);
          }
        }
      }

      // 스트림이 끝났지만 완료 이벤트를 받지 못한 경우
      if (result === null) {
        throw new Error('Stream ended without completion event');
      }

      return result;

    } finally {
      reader.releaseLock();
    }

  } catch (error) {
    console.error("Error processing request:", error);
    setProgress({ step: "error", percent: 0, message: "요청 처리 중 오류가 발생했습니다." });
    throw error;
  }
};
